import { CmsFontColor } from "@/components/tiptap/extensions/FontColor";
import { CmsFontFamily } from "@/components/tiptap/extensions/FontFamily";
import { Underline } from "@/components/tiptap/extensions/Underline";
import { FontSize } from "@/components/tiptap/TextStyle";
import { Button } from "@/components/ui/button";
import { CMS_FONT_SIZES_PX } from "@/lib/cms/cms-theme.constants";
import { Link } from "@tiptap/extension-link";
import Paragraph from "@tiptap/extension-paragraph";
import { TextAlign } from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { CustomHeading } from "@/components/tiptap/CustmHeading";
import { Indent } from "@/components/tiptap/Indent";
import ToolBar from "@/components/tiptap/ToolBar";

const SPACING_PARAGRAPH_DEFAULT = "spacing-paragraph-4";
const SPACING_PARAGRAPH_VALID = /^spacing-paragraph-(2|4|6)$/;

const CustomParagraph = Paragraph.extend({
  addAttributes() {
    return {
      class: {
        default: SPACING_PARAGRAPH_DEFAULT,
        parseHTML: (element: HTMLElement) => {
          const cls = element.getAttribute("class") || "";
          const match = cls.split(/\s+/).find((c) => SPACING_PARAGRAPH_VALID.test(c));
          return match ? match : SPACING_PARAGRAPH_DEFAULT;
        },
        renderHTML: (attributes: { class?: string }) => {
          const cls = attributes.class;
          const valid = typeof cls === "string" && SPACING_PARAGRAPH_VALID.test(cls);
          return { class: valid ? cls : SPACING_PARAGRAPH_DEFAULT };
        },
      },
    };
  },
});

export interface NotesEditorProps {
  content: string;
  onChange: (html: string) => void;
  onSave: () => void;
  disabled?: boolean;
}

export function NotesEditor({
  content,
  onChange,
  onSave,
  disabled = false,
}: NotesEditorProps) {
  const { t } = useTranslation();

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ paragraph: false, heading: false }),
      Indent,
      FontSize.configure({
        types: ["paragraph"],
        fontSize: [...CMS_FONT_SIZES_PX],
        defaultFontSize: "16px",
      }),
      TextStyle,
      CmsFontFamily,
      CmsFontColor,
      Underline,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { target: "_blank", rel: "noopener noreferrer" },
      }),
      CustomHeading.configure({ levels: [2, 3] }),
      CustomParagraph,
    ],
    content: "",
    editable: !disabled,
    editorProps: {
      attributes: {
        class:
          "min-w-0 w-full bg-white text-black font-['Instrument Sans'] min-h-[300px] p-8 focus:outline-none",
      },
    },
    onUpdate: ({ editor: e }) => onChange(e.getHTML()),
  });

  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content, false);
    }
  }, [editor, content]);

  if (!editor) return null;

  return (
    <div className="flex w-full flex-col gap-0 text-start antialiased pr-4">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <ToolBar editor={editor} toolbarPreset="complete" />
        <Button onClick={onSave} disabled={disabled} size="sm">
          {t("notes.save")}
        </Button>
      </div>
      <div className="bg-white border border-input rounded-md shadow-sm w-full max-w-2xl mx-auto overflow-hidden aspect-[9/21]">
        <EditorContent editor={editor} className={`h-full overflow-auto ${disabled ? "opacity-50" : ""}`} />
      </div>
    </div>
  );
}
